/**
 * @todo: Align this parameter selection process with swing foundation
 * parameter selection. One component
 */
import { Fragment, useCallback, useMemo } from 'react';

import { useAtomValue } from 'jotai';
import { useFormContext, useWatch } from 'react-hook-form';

import { BaseNode, Category, ParameterNode } from '@data';
import { useTranslate } from '@i18n';
import { useCategories, useParameterGroups, useUiParameters } from '@queries';
import { activityAtom, P_POSITION_REGEX } from '@store';
import { Box, Heading3, Heading4, Skeleton, u } from '@ui';

import { NewParameter } from '../../NewParameter/NewParameter';
import { filterParameters } from '../../ParameterFilter/parameterFilterUtils';

import * as css from './PositionGroup.css';

import type { ParameterFilterFormValues } from '../../ParameterFilter/ParameterFilterProvider';
import type { FilterSelectionKey } from '../../ParameterFilter/parameterFilters';

type ParameterGroupListProps = {
    activeSwingId: string | undefined;
    activeUserId: string | undefined;
    scheduleSync: (selectedParameterIds: string[]) => void;
};

const getMatchingParametersByGroup = (
    groups: readonly Category[],
    parameters: readonly ParameterNode[],
    filters: readonly FilterSelectionKey[],
    positions: readonly string[],
) =>
    Object.fromEntries(
        groups.map((group) => {
            const groupParameters = parameters.filter(({ categories }) => categories.swingFoundationGroup === group.id);
            const matchingParameters = filterParameters(groupParameters, filters, positions);

            return [group.id, matchingParameters];
        }),
    );

const toggleParameterId = (selectedParameterIds: string[], id: string) =>
    selectedParameterIds.includes(id)
        ? selectedParameterIds.filter((parameterId) => parameterId !== id)
        : [...selectedParameterIds, id];

export const ParameterGroupList = ({ activeSwingId, activeUserId, scheduleSync }: ParameterGroupListProps) => {
    const { control, setValue } = useFormContext<ParameterFilterFormValues>();
    const filters = useWatch({ control, name: 'filters' });
    const positions = useWatch({ control, name: 'positions' });
    const selectedParameterIds = useWatch({ control, name: 'selectedParameterIds' });
    const toggleParameter = useCallback(
        (id: string) => {
            const nextSelectedParameterIds = toggleParameterId(selectedParameterIds, id);
            setValue('selectedParameterIds', nextSelectedParameterIds, { shouldDirty: true });
            scheduleSync(nextSelectedParameterIds);
        },
        [scheduleSync, selectedParameterIds, setValue],
    );
    const isSelected = (id: string) => selectedParameterIds.includes(id);
    const { data: parameterGroups } = useParameterGroups();
    return parameterGroups.map((group) => (
        <PositionGroup
            key={group.id}
            id={group.id}
            i18n={group.i18n}
            activeSwingId={activeSwingId}
            activeUserId={activeUserId}
            filters={filters}
            positions={positions}
            toggleParameter={toggleParameter}
            isSelected={isSelected}
        />
    ));
};

type PositionGroupProps = Pick<BaseNode, 'id' | 'i18n'> &
    Pick<ParameterGroupListProps, 'activeSwingId' | 'activeUserId'> & {
        filters: readonly FilterSelectionKey[];
        positions: readonly string[];
        toggleParameter: (nodeId: string) => void;
        isSelected: (nodeId: string) => boolean;
    };

function PositionGroup({
    id,
    i18n,
    activeSwingId,
    activeUserId,
    toggleParameter,
    isSelected,
    filters,
    positions,
}: PositionGroupProps) {
    const activity = useAtomValue(activityAtom);
    const { data: parameters = [], isLoading: areParametersLoading } = useUiParameters();
    const { data: swingFoundationGroups = [], isLoading: areGroupsLoading } = useCategories('swing_foundation_group');
    const position = id.match(P_POSITION_REGEX)?.[0] ?? '';
    const t = useTranslate();

    const matchingParametersByGroup = useMemo(
        () => getMatchingParametersByGroup(swingFoundationGroups, parameters, filters, positions),
        [filters, parameters, positions, swingFoundationGroups],
    );

    const filteredSwingFoundationGroups = useMemo(
        () =>
            swingFoundationGroups.filter(
                (group) =>
                    matchingParametersByGroup[group.id]?.some(
                        (parameter) => parameter.parameter.filterTags?.includes(position),
                    ),
            ),
        [matchingParametersByGroup, position, swingFoundationGroups],
    );

    if (areParametersLoading || areGroupsLoading || !activity) {
        return <LoadingGroup />;
    }
    if (filteredSwingFoundationGroups.length === 0) {
        return null;
    }

    return (
        <Box mx={-16} px={u(2)} mb={u(2)} pb={u(2)} bg="panel" radius="md">
            <Heading3 paddingBlockStart={u(3)}>{t(i18n ?? '-')}</Heading3>
            {filteredSwingFoundationGroups.map((group) => (
                <Fragment key={group.id}>
                    <Heading4 py={u(2)}>{t(group.i18n ?? '-')}</Heading4>
                    <Box display="flex" gap={u(1.5)} flexWrap={'wrap'}>
                        {matchingParametersByGroup[group.id]?.map((parameter) => {
                            return (
                                <NewParameter
                                    key={parameter.id}
                                    isSelected={isSelected(parameter.id)}
                                    node={parameter}
                                    onClick={() => toggleParameter(parameter.id)}
                                    activityId={activity?.id}
                                    activeSwingId={activeSwingId}
                                    activeUserId={activeUserId}
                                />
                            );
                        })}
                    </Box>
                </Fragment>
            ))}
        </Box>
    );
}

const LoadingGroup = () => (
    <Box>
        <Skeleton shade={400} className={css.skeletonHeading} />
        <Box
            display="flex"
            alignItems="center"
            gap={u(1.5)}
            justifyContent="center"
            width="100%"
            height="max-content"
            paddingBlockStart={u(1)}
            paddingBlockEnd={u(2)}
        >
            <Skeleton shade={400} className={css.skeletonCard} flex={1} />
            <Skeleton shade={400} className={css.skeletonCard} flex={1} />
            <Skeleton shade={400} className={css.skeletonCard} flex={1} />
        </Box>
    </Box>
);
